(() => {
  if (customElements.get('marston-home-hero')) return;

  const FIRST_AUTOPLAY_DELAY = 5000;
  const AUTOPLAY_DELAY = 6000;
  const MANUAL_AUTOPLAY_DELAY = 9000;

  customElements.define('marston-home-hero', class extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.clearAutoplay?.();
      this.autoplayObserver?.disconnect();

      this.abort = new AbortController();
      const on = (el, type, fn, options = {}) => {
        if (el) el.addEventListener(type, fn, { ...options, signal: this.abort.signal });
      };

      this.scenes = [...this.querySelectorAll('.mhh-scene')];
      this.tabs = [...this.querySelectorAll('.mhh-tab')];
      if (!this.scenes.length || !this.tabs.length) return;

      this.autoplayTimer = null;
      this.autoplayDelay = FIRST_AUTOPLAY_DELAY;
      this.autoplayHovered = false;
      this.autoplayFocusPaused = false;
      this.autoplayInView = true;
      this.reduceMotionQuery = matchMedia('(prefers-reduced-motion: reduce)');

      this.index = Math.max(0, this.scenes.findIndex(scene => scene.hasAttribute('data-active')));
      this.show(this.index, false);

      this.tabs.forEach((tab, i) => on(tab, 'click', () => this.manualShow(i)));

      on(this, 'click', event => {
        const arrow = event.target.closest('[data-step]');
        if (!arrow) return;
        this.manualShow(this.index + Number(arrow.dataset.step));
      });

      const tabRail = this.querySelector('.mhh-tabs');
      on(tabRail, 'keydown', event => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        let next = this.index;
        if (event.key === 'Home') next = 0;
        else if (event.key === 'End') next = this.scenes.length - 1;
        else next += event.key === 'ArrowRight' ? 1 : -1;
        this.manualShow(next);
        this.tabs[this.index]?.focus();
      });

      on(this, 'mouseenter', () => {
        this.autoplayHovered = true;
        this.clearAutoplay();
      });

      on(this, 'mouseleave', () => {
        this.autoplayHovered = false;
        this.scheduleAutoplay(this.autoplayDelay);
      });

      on(this, 'focusin', event => {
        if (!event.target.matches?.(':focus-visible')) return;
        this.autoplayFocusPaused = true;
        this.clearAutoplay();
      });

      on(this, 'focusout', () => {
        requestAnimationFrame(() => {
          if (!this.isConnected) return;
          const active = document.activeElement;
          const keyboardFocusInside = active && this.contains(active) && active.matches?.(':focus-visible');
          this.autoplayFocusPaused = Boolean(keyboardFocusInside);
          if (!this.autoplayFocusPaused) this.scheduleAutoplay(this.autoplayDelay);
        });
      });

      on(document, 'visibilitychange', () => {
        if (document.hidden) this.clearAutoplay();
        else this.scheduleAutoplay(this.autoplayDelay);
      });

      on(this.reduceMotionQuery, 'change', () => {
        if (this.reduceMotionQuery.matches) this.clearAutoplay();
        else this.scheduleAutoplay(this.autoplayDelay);
      });

      on(this, 'shopify:block:select', event => {
        const i = this.scenes.findIndex(scene => scene.dataset.block === event.detail.blockId);
        if (i >= 0) this.show(i, false);
      });

      this.scenes.forEach(scene => {
        const art = scene.querySelector('[data-swipe]');
        if (!art) return;
        let start = null;

        on(art, 'pointerdown', event => {
          if (event.pointerType === 'touch' || event.pointerType === 'pen') {
            start = { x: event.clientX, y: event.clientY };
          }
        });

        on(art, 'pointerup', event => {
          if (!start) return;
          const dx = event.clientX - start.x;
          const dy = event.clientY - start.y;
          start = null;
          if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.4) {
            this.manualShow(this.index + (dx < 0 ? 1 : -1));
          }
        });

        on(art, 'pointercancel', () => { start = null; });

        on(art, 'pointermove', event => {
          if (
            this.dataset.motion !== 'true' ||
            event.pointerType !== 'mouse' ||
            !matchMedia('(min-width:900px) and (prefers-reduced-motion:no-preference)').matches ||
            navigator.connection?.saveData
          ) return;

          const rect = art.getBoundingClientRect();
          art.style.setProperty('--mhh-x', ((event.clientX - rect.left) / rect.width - .5) * 14 + 'px');
          art.style.setProperty('--mhh-y', ((event.clientY - rect.top) / rect.height - .5) * 10 + 'px');
        });

        on(art, 'pointerleave', () => {
          art.style.removeProperty('--mhh-x');
          art.style.removeProperty('--mhh-y');
        });
      });

      if ('IntersectionObserver' in window) {
        this.autoplayObserver = new IntersectionObserver(entries => {
          this.autoplayInView = entries[0]?.isIntersecting ?? true;
          if (this.autoplayInView) this.scheduleAutoplay(this.autoplayDelay);
          else this.clearAutoplay();
        }, { threshold: 0.15 });
        this.autoplayObserver.observe(this);
      }

      this.scheduleAutoplay(FIRST_AUTOPLAY_DELAY);
    }

    manualShow(index) {
      this.autoplayDelay = MANUAL_AUTOPLAY_DELAY;
      this.show(index, true);
      this.scheduleAutoplay(MANUAL_AUTOPLAY_DELAY);
    }

    clearAutoplay() {
      if (this.autoplayTimer) {
        clearTimeout(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    }

    canAutoplay() {
      return (
        this.scenes?.length > 1 &&
        this.dataset.motion === 'true' &&
        !window.Shopify?.designMode &&
        !this.reduceMotionQuery?.matches &&
        !navigator.connection?.saveData &&
        !document.hidden &&
        this.autoplayInView !== false &&
        !this.autoplayHovered &&
        !this.autoplayFocusPaused
      );
    }

    scheduleAutoplay(delay = AUTOPLAY_DELAY) {
      this.clearAutoplay();
      if (!this.canAutoplay()) return;

      this.autoplayTimer = setTimeout(() => {
        this.autoplayTimer = null;
        if (!this.canAutoplay()) return;
        this.show(this.index + 1, false);
        this.autoplayDelay = AUTOPLAY_DELAY;
        this.scheduleAutoplay(AUTOPLAY_DELAY);
      }, delay);
    }

    show(index, announce = true) {
      const count = this.scenes.length;
      this.index = (index + count) % count;

      this.scenes.forEach((scene, i) => {
        const active = i === this.index;
        scene.toggleAttribute('data-active', active);
        scene.setAttribute('aria-hidden', String(!active));
        scene.inert = !active;
      });

      this.tabs.forEach((tab, i) => tab.setAttribute('aria-pressed', String(i === this.index)));

      const counter = this.querySelector('[data-counter]');
      if (counter) {
        counter.textContent = String(this.index + 1).padStart(2, '0') + ' / ' + String(count).padStart(2, '0');
      }

      const activeScene = this.scenes[this.index];
      const ink = activeScene?.style.getPropertyValue('--mhh-ink');
      if (ink) this.style.setProperty('--mhh-ink', ink);

      const image = activeScene?.querySelector('img');
      if (image) image.loading = 'eager';

      const tab = this.tabs[this.index];
      const rail = tab?.parentElement;
      if (rail && tab) {
        rail.scrollLeft = tab.offsetLeft - rail.offsetLeft - (rail.clientWidth - tab.clientWidth) / 2;
      }

      if (announce) {
        const label = this.tabs[this.index]?.textContent.trim() || '';
        const live = this.querySelector('[data-live]');
        if (live) live.textContent = label;
      }
    }

    disconnectedCallback() {
      this.clearAutoplay();
      this.autoplayObserver?.disconnect();
      this.abort?.abort();
    }
  });
})();
